import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { UserDto } from "@platform/core";
import { Alert, Button, Dialog, Field, PasswordInput } from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi } from "../../context";
import { useT } from "../../i18n";
import { CopyButton } from "../../lib/copy";
import { errorText } from "../../lib/errors";

/**
 * Admin sets a user's password: generated by the server (shown once) or typed. The user's sessions are revoked and
 * they must choose their own password at the next sign-in (docs/security.md).
 */
export function SetPasswordDialog({
  user,
  open,
  onOpenChange,
}: {
  user: UserDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useT();
  const api = useApi();
  const [mode, setMode] = useState<"generate" | "type">("generate");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState<{ generated?: string } | null>(null);
  const save = useMutation({
    mutationFn: () => api.users.setPassword(user.id, mode === "type" ? password : undefined),
    onSuccess: (r) => setResult(r.password ? { generated: r.password } : {}),
  });
  const close = () => {
    onOpenChange(false);
    setMode("generate");
    setPassword("");
    setConfirm("");
    setResult(null);
    save.reset();
  };
  const mismatch = mode === "type" && confirm.length > 0 && confirm !== password;
  const fieldErrors = isApiError(save.error) ? save.error.fieldErrors() : {};
  const who = user.userCode ?? user.username ?? user.email;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && close()}
      title={t("user.setPassword.title")}
      description={who}
      closeLabel={t("common.close")}
      footer={
        result ? (
          <Button data-testid="set-password-done" onClick={close}>
            {t("common.close")}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="set-password-submit"
              loading={save.isPending}
              disabled={mode === "type" && (!password || confirm !== password)}
              onClick={() => save.mutate()}
            >
              {t("user.setPassword.submit")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div>
          <Alert tone="success" className="mb-4">
            {t("user.setPassword.done")}
          </Alert>
          {result.generated && (
            <>
              <Alert tone="warning" className="mb-3">
                {t("user.setPassword.once")}
              </Alert>
              <div className="flex items-center gap-2 rounded bg-hover px-3 py-2">
                <code data-testid="generated-password" className="flex-1 font-mono text-sm break-all">
                  {result.generated}
                </code>
                <CopyButton value={result.generated} />
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          {save.isError && !Object.keys(fieldErrors).length && (
            <Alert tone="error" className="mb-4">
              {errorText(save.error)}
            </Alert>
          )}
          <div className="mb-4 flex flex-col gap-2 text-sm" role="radiogroup">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="password-mode"
                checked={mode === "generate"}
                onChange={() => setMode("generate")}
              />
              {t("user.setPassword.generate")}
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="password-mode"
                data-testid="set-password-type"
                checked={mode === "type"}
                onChange={() => setMode("type")}
              />
              {t("user.setPassword.type")}
            </label>
          </div>
          {mode === "type" && (
            <>
              <Field label={t("user.setPassword.new")} error={fieldErrors.password}>
                {(p) => (
                  <PasswordInput
                    {...p}
                    showLabel={t("common.showPassword")}
                    hideLabel={t("common.hidePassword")}
                    autoComplete="new-password"
                    data-testid="set-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Field
                label={t("user.setPassword.confirm")}
                error={mismatch ? t("user.setPassword.mismatch") : undefined}
              >
                {(p) => (
                  <PasswordInput
                    {...p}
                    showLabel={t("common.showPassword")}
                    hideLabel={t("common.hidePassword")}
                    autoComplete="new-password"
                    data-testid="set-password-confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                )}
              </Field>
            </>
          )}
          <p className="mt-2 text-xs text-ink-2">{t("user.setPassword.hint")}</p>
        </div>
      )}
    </Dialog>
  );
}
